import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

/** A small panel anchored to the button that opened it (M14 round six: a failed source's "Why it failed"). It sits below the button, or
 *  above it when there is no room, never past the window's edges; Escape, a click outside it or one of its actions closes it, and the
 *  focus goes back to the button. Ephemeral: nothing about it belongs in the URL. */
export function Popover({ label, anchor, onClose, children, width = 320 }: { label: string; anchor: RefObject<HTMLElement | null>; onClose: () => void; children: ReactNode; width?: number }) {
  const panel = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number; width: number } | null>(null);
  const place = useCallback(() => {
    const a = anchor.current?.getBoundingClientRect(); const h = panel.current?.offsetHeight ?? 0;
    if (!a) return;
    const gap = 6, edge = 8, w = Math.min(width, window.innerWidth - 2 * edge);
    const left = Math.max(edge, Math.min(a.right - w, window.innerWidth - w - edge));
    const below = a.bottom + gap;
    const top = below + h <= window.innerHeight - edge ? below : Math.max(edge, a.top - gap - h);
    setAt({ top, left, width: w });
  }, [anchor, width]);
  const close = useRef(onClose); close.current = onClose;
  useLayoutEffect(() => { place(); }, [place]); // the panel is laid out (hidden) by now, so its real height is known
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (!panel.current?.contains(t) && !anchor.current?.contains(t)) close.current(); };
    window.addEventListener("keydown", onKey); document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    panel.current?.focus({ preventScroll: true });
    const back = anchor.current;
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); back?.focus({ preventScroll: true }); };
  }, [anchor, place]);
  return createPortal(
    <div ref={panel} className="ob-popover" role="dialog" aria-label={label} tabIndex={-1} style={at ? { top: at.top, left: at.left, width: at.width } : { visibility: "hidden" }}>{children}</div>,
    document.body);
}
